import React, {FC, useEffect, useState} from 'react'
import { useHistory } from 'react-router'
import moment from 'moment';

import { TopBarRedesigned } from "components/TopBarRedesigned";
import { formatPartialOfferTime, debugTimeFormatting } from "utils/timeFormatting";
import {exitReact, formatProfileHygienistName, getPaymentTerms, getPaymentType} from "utils";
import { PartialOffer, UserAccountProps, JobPropsWithHygienist} from "utils/types";
import {
    BasicContainer,
    ClickContainer,
    PaymentTerms, SimpleButton,
    Title,
    TitleContainer,
    Value,
    ValueBox,
    Subtitle,
    UserContainerWithBorder, RightContainer, LeftContainer, TopContainer, TermsContainer, DashedCircle,
    PartialHoursContainer
} from "./CustomOffersPage.styles";
import dayjs from "dayjs";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import styles from "../../hygienist/ShiftDetailPage/ShiftDetail.module.scss";

import FixedLoader from "../../../components/FixedLoader";
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";
import _ from "lodash";

import {Icon} from "../../hygienist/AvailableJobsPage/AvailableJobsPage.styles";
import heartFilled from "../../../images/heartFilled.svg";
import heartUnfilled from "../../../images/heartUnfilled.svg";
import ProImage from "../../../components/Icons/ProImage";
import EliteImage from "../../../components/Icons/EliteImage";

import BookAnotherTemp from "../CustomOfferDetailsPage/components/BookAnotherTemp";
import RemoveShift from "../CustomOfferDetailsPage/components/RemoveShift";
import {Analytics} from "utils/analytics";
import {JOB_STATUSES, PARTIAL_OFFER_STATUS} from "../../../utils/constants";
import * as ls from "local-storage";
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import {SimpleModal} from "../../../components/Modal";
import { usePersistedState } from 'hooks';
import { entities } from 'reduxStore';

interface OfferIsRemovedModalProps {
    firstName: string,
    onConfirm: () => void
}

const OfferIsRemovedModal: FC<OfferIsRemovedModalProps> = ({firstName, onConfirm}) => {
    return (
        <>
            <h2>This Offer Is No Longer Available</h2>
            <div>
                This offer is no longer available. {firstName} was booked by another office for this date.<br/><br/>
                Hygienists and assistants can have multiple offers submitted to various offices for the same date.<br/><br/>
                The first office who Accepts their offer books them for that shift, which removes all other offers (to prevent double-booking).
            </div>
            <div>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                >
                    Ok
                </StyledButton>
            </div>
        </>
    )
}

const AddUpdatePaymentModal: FC<any> = ({tempAmount, onClose, goPaymentPage}) => {
    const onConfirm = async () => {
        Analytics.track('add_payment_details', {add_payment_details: 'payment-info'});
        goPaymentPage()
    }

    const onCancel = () => {
        Analytics.track('add_payment_details', {add_payment_details: 'cancel'})
        onClose()
    }

    return (
        <>
            <h2>Add/Update Payment Details</h2>
            <div>
                Almost there!  Just one more thing - to view the details of this offer, we require up-to-date payment details.<br/><br/>
                Don't worry - we only ever process our placement fee of {Number(tempAmount) / 100} after a temping shift is completed - no surprises.<br/><br/>
                Since 2015, TempStars has completed over 100,000 temping shifts - so we know what we're doing, and we've got you.<br/><br/>
                Add/Update Payment Details Now?
            </div>
            <div>
                <StyledButton variant="outlined" onClick={onCancel}>
                    No
                </StyledButton>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                >
                    Yes
                </StyledButton>
            </div>
        </>
    )
}

const CustomOffersPage: FC = () => {
    const history = useHistory()
    const [isLoading, setIsLoading] = useState(false);

    const {id: jobId} = history.location.state as { id: number }

    const [job, setJob] = useState<JobPropsWithHygienist>()
    const [offers, setOffers] = useState<PartialOffer[]>(job?.partialOffers || [])
    const [isRemovedOfferModalOpened, setIsRemovedOfferModalOpened] = useState(false)
    const [isPaymentModalOpen, setPaymentModalOpen] = useState(false)
    const [selectedOffer, setSelectedOffer] = useState<PartialOffer>()
    const [user, setUser] = useState<UserAccountProps>()

    const location = user?.locations?.find(location => location.id === job?.locationId)

    const setUserEntity = usePersistedState({
		entityName: entities.user,
	})[1];

    const [getJobDetailsLoading, , getJobDetails] = useActionApi({
        apiMethod: Api.getJobDetails,
        onSuccess: (jobObj: any) => {
            if(_.isEmpty(jobObj)) return
            setJob(jobObj)
            const filteredOffers = _.filter(jobObj.partialOffers, (o) =>
                (o.status === PARTIAL_OFFER_STATUS.POSTED
                    || o.status === PARTIAL_OFFER_STATUS.EXPIRED
                    || o.status === PARTIAL_OFFER_STATUS.REMOVED)
                && o.isResubmitRequested === 0)
            setOffers(filteredOffers)
        },
    });

    const [getUserLoading, , getUserEntity] = useActionApi({
        apiMethod: Api.getUser,
        onSuccess: (user: UserAccountProps) => {
            setUser(user)
            setUserEntity(user)
        },
    });

    const goToModifyJob = () => {
        history.push('modifyJob', { job })
    }

    const goHome = () => {
        history.push('loading')
        exitReact('home')
    }

    useEffect(() => {
        if (jobId) {
            getJobDetails(jobId)
            getUserEntity({})
        }
    }, [])

    const onOpenOffer = (id: number, partialOffer: any) => {
        setSelectedOffer(partialOffer)

        if (partialOffer.status === PARTIAL_OFFER_STATUS.REMOVED) {
            setIsRemovedOfferModalOpened(true)
        } else if ((!location?.stripeCustomerId || !location?.isCardValid) && location?.isCentralBooked !== 2) {
            setPaymentModalOpen(true)
        } else {
            history.push('customOfferDetails', {offerId: id, job, partialOffer})
        }
    }

    const goPaymentPage = () => {
        history.push('loading')
        exitReact('payment-info', {locationId: job?.locationId})
    }

    const onRemoveOffer = async (partialOfferId: number) => {
        setIsRemovedOfferModalOpened(false)
        setIsLoading(true)
        await Api.removePartialOffer(partialOfferId)
        setIsLoading(false)
        getJobDetails(jobId)
    }

    return (
        <>
            <TopBarRedesigned
                onBack={goHome}
                title={'Custom Offers'}
            />

            <SimpleModal isOpen={isRemovedOfferModalOpened} onClose={() => setIsRemovedOfferModalOpened(false)}>
                {selectedOffer && <OfferIsRemovedModal
                    firstName={selectedOffer?.hygienist.firstName}
                    onConfirm={() => onRemoveOffer(selectedOffer?.id)}
                />}
            </SimpleModal>

            <SimpleModal isOpen={isPaymentModalOpen} onClose={() => setPaymentModalOpen(false)}>
                <AddUpdatePaymentModal
                    tempAmount={user?.dentist?.tempAmount}
                    onClose={() => setPaymentModalOpen(false)}
                    goPaymentPage={goPaymentPage}
                />
            </SimpleModal>

            {job && <BasicContainer>
                <TitleContainer>
                    <Title>
                        <div>
                            <div>Dental {job?.type ? 'Assistant' : 'Hygienist'} Shift</div>
                            <Subtitle>
                                {`${job?.location.address}, ${job?.location.city}, ${job?.location.province} ${job?.location.postalCode}`}
                            </Subtitle>

                            <ValueBox>
                                <DateRangeOutlinedIcon className={styles.shiftDateIcon}/>
                                {dayjs(job?.shifts[0].shiftDate).format('dddd, MMM D')}
                            </ValueBox>
                            <ValueBox>Start: <Value>
                                {job?.shifts[0].postedStart}
                            </Value>|   End: <Value>
                                {job?.shifts[0].postedEnd}
                            </Value>
                            </ValueBox>
                            <SimpleButton onClick={goToModifyJob}>Change hours</SimpleButton>

                            <ValueBox>
                                Proposed Rate: <Value>${job?.proposedRate}/hr</Value>
                            </ValueBox>
                        </div>
                    </Title>
                    <TermsContainer>
                        <div>Payment terms: {getPaymentTerms(job?.paymentTerms)}</div>
                        {job.location.country ? <div>Working Relationship: {getPaymentType(job.paysW2)}</div> : null}
                    </TermsContainer>

                    <a href="https://www.tempstars.com/direct_payment" target="_system"
                       className="external why-link">Just a friendly reminder</a>
                </TitleContainer>

                {job?.Autobook !== 1 && offers.map((offer, index) => {
                    const hygienist = offer.hygienist
                    const professionalLicense = _.isEmpty(hygienist)
                        ? '' : formatProfileHygienistName(hygienist, hygienist?.skills);
                    const hourlyRate = `$${offer?.hourlyRate}/hr`

                    const expiredOffer = offer.status === PARTIAL_OFFER_STATUS.REMOVED
                        || offer.status === PARTIAL_OFFER_STATUS.EXPIRED
                        || job?.status === JOB_STATUSES.OFFER_EXPIRED

                    const isPartialHours = offer.offeredStartTime !== job?.shifts[0].postedStart || 
                                         offer.offeredEndTime !== job?.shifts[0].postedEnd;

                    if ((process.env.NODE_ENV as string) === 'staging' || (process.env.NODE_ENV as string) === 'local') {
                      debugTimeFormatting(
                        offer.offeredStartTime,
                        offer.offeredStartTimeLocal,
                        'CustomOffersPage - Offer Times'
                      );
                    }

                    const formattedStartTime = formatPartialOfferTime(
                      offer.offeredStartTime, 
                      offer.offeredStartTimeLocal,
                      offer.location?.lat,
                      offer.location?.lon
                    );
                    const formattedEndTime = formatPartialOfferTime(
                      offer.offeredEndTime, 
                      offer.offeredEndTimeLocal,
                      offer.location?.lat,
                      offer.location?.lon
                    );
                    
                    if (offer?.isResubmitRequested) {
                        return
                    }

                    return (
                        <ClickContainer onClick={() => onOpenOffer(offer?.id, offer)}>
                            <UserContainerWithBorder id={index + hygienist?.lastName} style={{opacity: expiredOffer ? '0.6' : '1'}}>
                                <TopContainer>
                                    <LeftContainer>
                                        <Value style={{display: 'flex', gap: '0.5rem'}}>
                                            {professionalLicense}
                                            <Icon style={{height: 22, alignSelf: 'center'}}
                                                  src={offer?.isFavouriteHygienist ? heartFilled : heartUnfilled}/>
                                            {hygienist?.status === 1 &&
                                                <ProImage style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                                            {hygienist?.status === 2 && <EliteImage
                                                style={{display: 'flex', alignSelf: 'center', margin: 'unset'}}/>}
                                        </Value>

                                        <Subtitle>
                                            Graduated: {hygienist.graduationYear}
                                        </Subtitle>
                                        {isPartialHours && (
                                            <PartialHoursContainer>
                                                <DashedCircle />
                                                <div style={{color: '#666', marginTop: '4px'}}>
                                                    Partial Hours Offered: {formattedStartTime} - {formattedEndTime}
                                                </div>
                                            </PartialHoursContainer>
                                        )}
                                    </LeftContainer>

                                    <RightContainer>
                                        <Value>{hourlyRate}</Value>View Details<i className="icon icon-forward"/>
                                    </RightContainer>
                                </TopContainer>
                            </UserContainerWithBorder>
                        </ClickContainer>)
                })}

                <div style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <BookAnotherTemp job={job}/>
                    {job?.Autobook === 1 && !job?.cancelledOffer
                        ? <div className="center-box" style={{textAlign: 'center', marginTop: '20px'}}>
                            You have submitted an Insta-Book Request to {job?.hygienist?.firstName} which will be confirmed or possibly declined within 15min.<br/>
                        If you must cancel this shift, you'll have to wait until after the Insta-Book Request is accepted or declined.<br/>
                        Watch your e-mail over the next 20min for notification about this Insta-Book Request status.
                    </div>
                    : <RemoveShift job={job}/>}
                </div>
            </BasicContainer>}

            <FixedLoader loading={getJobDetailsLoading || isLoading || getUserLoading}/>
        </>
    )
}

export default CustomOffersPage
